'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { Button } from '../../../components/button';
import { Field } from '../../../components/field';
import { createSelectListCollection, Select } from '../../../components/select';
import { BodyDefault } from '../../../components/typography';
import { toasterApi } from '../../toast/ToastProvider';
import {
  buildInternalAccessBindingPayload,
  findConfiguredInternalNetworkLabel,
  isInternalAccessBindingDirty,
  resolvePersistedInternalNetworkValue,
  type ApplicationInternalAccess,
  type InternalAccessBindingPayload,
  type InternalNetworkOption,
  type InternalNetworkTransportFlags,
} from './internal-access.model';

export type InternalAccessBindingEditorProps = {
  internalAccess: ApplicationInternalAccess | null;
  networkOptions: InternalNetworkOption[];
  flags: InternalNetworkTransportFlags;
  areFeatureFlagsResolved: boolean;
  isNetworksLoading: boolean;
  isReadOnly?: boolean;
  isPendingEnable?: boolean;
  isSaving?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onSaveBinding: (payload: InternalAccessBindingPayload) => Promise<unknown>;
};

export function InternalAccessBindingEditor({
  internalAccess,
  networkOptions,
  flags,
  areFeatureFlagsResolved,
  isNetworksLoading,
  isReadOnly,
  isPendingEnable = false,
  isSaving,
  onDirtyChange,
  onSaveBinding,
}: InternalAccessBindingEditorProps) {
  const t = useTranslations(
    'designSystem.applicationSettings.networking.internalAccess',
  );
  const tCommon = useTranslations('designSystem.common');
  const [selectedValue, setSelectedValue] = useState('');
  const singleNetwork =
    networkOptions.length === 1 ? networkOptions[0] : undefined;
  const persistedValue = resolvePersistedInternalNetworkValue({
    internalAccess,
    flags,
  });

  useEffect(() => {
    if (!areFeatureFlagsResolved) {
      return;
    }

    setSelectedValue(persistedValue || singleNetwork?.value || '');
  }, [areFeatureFlagsResolved, persistedValue, singleNetwork?.value]);

  const networkCollection = useMemo(
    () =>
      createSelectListCollection({
        items: networkOptions,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
      }),
    [networkOptions],
  );
  const isBindingDirty = isInternalAccessBindingDirty({
    selectedValue,
    persistedValue,
    internalAccess,
    isPendingEnable,
  });
  const canSave = Boolean(selectedValue) && isBindingDirty && !isSaving;

  useEffect(() => {
    onDirtyChange?.(isBindingDirty);
  }, [isBindingDirty, onDirtyChange]);

  const handleSave = async () => {
    const payload = buildInternalAccessBindingPayload({ selectedValue, flags });

    if (!payload) {
      return;
    }

    try {
      await onSaveBinding(payload);
      toasterApi.success({ title: t('saveSuccess') });
    } catch (error) {
      console.error(new Error('Failed to save internal access binding'), error);
      toasterApi.error({ title: t('saveFailed') });
    }
  };

  const saveButton = (
    <Button
      size="md"
      flexShrink={0}
      onClick={() => {
        void handleSave();
      }}
      disabled={!canSave}
      loading={isSaving}
    >
      {tCommon('saveChanges')}
    </Button>
  );

  if (isReadOnly) {
    if (!internalAccess) {
      return <BodyDefault color="fg.muted">{t('notConfigured')}</BodyDefault>;
    }

    const configuredLabel = findConfiguredInternalNetworkLabel({
      internalAccess,
      options: networkOptions,
    });

    return (
      <VStack gap={1} alignItems="start">
        {configuredLabel ? (
          <BodyDefault>
            {t('network')}: {configuredLabel}
          </BodyDefault>
        ) : null}
      </VStack>
    );
  }

  if (!isNetworksLoading && networkOptions.length === 0) {
    return null;
  }

  if (singleNetwork) {
    return (
      <HStack alignItems="center" gap={2}>
        {saveButton}
      </HStack>
    );
  }

  return (
    <Field.Root alignSelf="flex-start" w="full" maxW="md">
      <Field.Label>{t('network')}</Field.Label>
      <HStack alignItems="center" gap={2} w="fit-content">
        <Box w="280px">
          <Select.Root
            collection={networkCollection}
            value={selectedValue ? [selectedValue] : []}
            onValueChange={({ value }) => setSelectedValue(value[0] ?? '')}
            disabled={isNetworksLoading}
            size="md"
            positioning={{
              sameWidth: true,
              strategy: 'fixed',
              placement: 'bottom-start',
            }}
            width="full"
          >
            <Select.Control w="full">
              <Select.Trigger aria-label={t('network')}>
                <Select.ValueText
                  placeholder={t('selectNetwork')}
                  fontSize="sm"
                />
                <ArrowUpDownIcon aria-hidden="true" />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup>
                  {networkCollection.items.map((network) => (
                    <Select.Item key={network.value} item={network}>
                      <Select.ItemText fontSize="sm">
                        {network.label}
                      </Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon aria-hidden="true" />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
        {saveButton}
      </HStack>
    </Field.Root>
  );
}
